import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import SbrDrawer from '../sbr/SbrDrawer';
import PhoneInput from '../forms/phoneInput';
import {
    createPrivateIntakeLink, getIntakeLink, getPrivateIntakeLinks, regenerateIntakeLink, revokePrivateIntakeLink,
} from '../../services/clientIntakes';
import { buildViberInvoiceUrl, buildWhatsAppInvoiceUrl } from '../../utils/invoiceMessaging';
import { toLocalDateString } from '../../utils/helper';
import { onMutateSuccess } from '../../utils/queryResponse';
import { isUserAdmin } from '../../utils/roleHelpers';
import ConfirmationModal from './confirmation';
import { toast } from '../../utils/toast';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';

const LinkIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
);

const styles = `
  .sbr .ilk-url{display:flex;gap:8px;align-items:center;margin:10px 0 6px}
  .sbr .ilk-url input{flex:1;border:1px solid var(--line);border-radius:10px;padding:10px 12px;font:600 13px inherit;color:var(--ink);background:var(--surface,#f8fafc);min-width:0}
  .sbr .ilk-msg{width:100%;border:1px solid var(--line);border-radius:10px;padding:10px 12px;font:500 13.5px inherit;color:var(--ink);resize:vertical;min-height:110px}
  .sbr .ilk-share{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:10px}
  .sbr .ilk-share.four{grid-template-columns:1fr 1fr 1fr 1fr}
  .sbr .ilk-share a,.sbr .ilk-share button{display:inline-flex;align-items:center;justify-content:center;padding:10px 8px;border:1px solid var(--line);border-radius:10px;background:#fff;color:var(--brand-ink);font:800 12px inherit;text-decoration:none;cursor:pointer}
  .sbr .ilk-hint{font-size:12.5px;color:var(--muted);line-height:1.5;margin-top:12px}
  .sbr .ilk-sec{margin-top:24px;padding-top:18px;border-top:1px solid var(--line)}
  .sbr .ilk-sec h4{font-size:13.5px;font-weight:800;color:var(--ink);margin:0 0 4px}
  .sbr .ilk-sec .sub{font-size:12.5px;color:var(--muted);line-height:1.5;margin-bottom:12px}
  .sbr .ilk-fields{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  @media(max-width:520px){.sbr .ilk-fields{grid-template-columns:1fr}}
  .sbr .ilk-fields label{display:flex;flex-direction:column;gap:4px;font-size:11.5px;font-weight:800;color:var(--muted)}
  .sbr .ilk-fields input{border:1px solid var(--line);border-radius:10px;padding:9px 11px;font:600 13px inherit;color:var(--ink);background:#fff;min-width:0}
  .sbr .ilk-new{margin-top:12px;padding:12px;border-radius:12px;background:var(--brand-bg,#eff6ff);border:1px solid var(--brand-bd,#bfdbfe)}
  .sbr .ilk-new .exp{font-size:12px;color:var(--muted);font-weight:600;margin-top:6px}
  .sbr .ilk-list{margin-top:14px;display:flex;flex-direction:column;gap:6px}
  .sbr .ilk-row{display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--line);border-radius:10px;background:#fff}
  .sbr .ilk-row .lb{font-size:13px;font-weight:700;color:var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .sbr .ilk-row .mt{font-size:11.5px;color:var(--muted);font-weight:600}
  .sbr .ilk-row .st{font-size:10.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;padding:2px 7px;border-radius:999px;background:var(--surface,#f8fafc);color:var(--muted);border:1px solid var(--line)}
  .sbr .ilk-row .st.active{color:#15803d;background:#dcfce7;border-color:transparent}
  .sbr .ilk-row .st.used{color:#1d4ed8;background:#dbeafe;border-color:transparent}
  .sbr .ilk-row .rv{margin-left:auto;border:0;background:transparent;color:var(--bad);font:800 12px inherit;cursor:pointer;padding:4px 6px}
`;

// Customer-facing texts, built in the active language each time they are used.
const DEFAULT_MESSAGE = (url) => i18n.t('intakeLink.defaultMessage', { url });
const PRIVATE_MESSAGE = (url, days) => i18n.t('intakeLink.privateMessage', { url, count: days });
const EMAIL_SUBJECT = () => i18n.t('intakeLink.emailSubject');

const copyText = async (value) => {
    try {
        await navigator.clipboard.writeText(value);
        toast.success(i18n.t('intakeLink.linkCopied'), { position: toast.POSITION.BOTTOM_RIGHT });
    } catch {
        toast.error(i18n.t('intakeLink.copyFailed'), { position: toast.POSITION.BOTTOM_RIGHT });
    }
};

const daysUntil = (iso) => Math.max(1, Math.round((new Date(iso).getTime() - Date.now()) / 86400000));

/**
 * Private, single-use link for one phone enquiry. The office types an
 * optional label (e.g. the caller's name) which is stored only on the link
 * record for the office list — the URL itself carries a random token and
 * nothing else. Optional phone: opens WhatsApp / Viber straight to that
 * number, otherwise the share sheet without a recipient.
 */
const PrivateLinkSection = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    // Confirmations use the app's own modal: a window.confirm is unstyled and,
    // in a drawer, appears detached from the thing being confirmed.
    const [linkToRevoke, setLinkToRevoke] = useState(null);
    const [label, setLabel] = useState('');
    const [phone, setPhone] = useState('');
    const [created, setCreated] = useState(null);

    const { data: listData, isLoading: listLoading } = useQuery(['intake-links'], getPrivateIntakeLinks);
    const links = listData?.code === 200 && Array.isArray(listData.message) ? listData.message : [];
    const active = links.filter((l) => l.status === 'active');
    const recent = links.filter((l) => l.status !== 'active').slice(0, 5);

    const { mutate: create, isLoading: creating } = useMutation(createPrivateIntakeLink, {
        onSuccess: onMutateSuccess((link) => {
            setCreated(link);
            queryClient.invalidateQueries(['intake-links']);
        }, t('intakeLink.privateLinkCreated')),
    });
    const { mutate: revoke } = useMutation(revokePrivateIntakeLink, {
        onSuccess: onMutateSuccess((link) => {
            if (created?.id === link?.id) setCreated(null);
            queryClient.invalidateQueries(['intake-links']);
        }, t('intakeLink.linkRevoked')),
    });

    const text = created ? PRIVATE_MESSAGE(created.url, daysUntil(created.expiresAt)) : '';

    // `l.status` stays the CSS class; only the visible word is translated.
    const statusLabel = (status) => {
        if (status === 'used') return t('intakeLink.used');
        if (status === 'revoked') return t('trackingLink.revoked');
        if (status === 'expired') return t('trackingLink.expired');
        return status;
    };

    return (
        <div className="ilk-sec">
            <h4>{t('intakeLink.privateTitle')}</h4>
            <div className="sub">
                <Trans i18nKey="intakeLink.privateIntro" components={{ b: <b /> }} />
            </div>
            <div className="ilk-fields">
                <label>{t('intakeLink.labelField')}
                    <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('intakeLink.optionalPlaceholder')} maxLength={100} />
                </label>
                <label>{t('intakeLink.phoneField')}
                    {/* E.164 matters here: WhatsApp and Viber only open the right
                        conversation for a number not in contacts if it carries
                        the country code. */}
                    <PhoneInput value={phone} onChange={setPhone} placeholder={t('intakeLink.optionalPlaceholder')} />
                </label>
            </div>
            <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" style={{ marginTop: 10 }} disabled={creating}
                onClick={() => create({ label: label.trim() || undefined })}>
                {creating ? t('intakeLink.creating') : t('intakeLink.generatePrivate')}
            </button>

            {created && (
                <div className="ilk-new">
                    {created.label && <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink)' }}>{created.label}</div>}
                    <div className="ilk-url" style={{ margin: '6px 0 0' }}>
                        <input readOnly value={created.url} onFocus={(e) => e.target.select()} />
                    </div>
                    <div className="exp">{t('intakeLink.singleUse')} · {t('trackingLink.expires', { date: toLocalDateString(created.expiresAt, 'dd.MM.yyyy HH:mm') })}</div>
                    <div className="ilk-share four">
                        <a href={buildWhatsAppInvoiceUrl(phone, text)} target="_blank" rel="noreferrer">WhatsApp</a>
                        <a href={buildViberInvoiceUrl(phone, text)}>Viber</a>
                        <a href={`mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT())}&body=${encodeURIComponent(text)}`}>{t('common.email')}</a>
                        <button type="button" onClick={() => copyText(created.url)}>{t('intakeLink.copyLink')}</button>
                    </div>
                    <div className="ilk-hint" style={{ marginTop: 8 }}>{t('intakeLink.linkShownOnce')}</div>
                </div>
            )}

            <div className="ilk-list">
                {listLoading && <div className="mt" style={{ fontSize: 12, color: 'var(--muted)' }}>{t('intakeLink.loadingLinks')}</div>}
                {!listLoading && !active.length && !recent.length && <div style={{ fontSize: 12, color: 'var(--faint)' }}>{t('intakeLink.noPrivateLinks')}</div>}
                {active.map((l) => (
                    <div className="ilk-row" key={l.id}>
                        <span className="st active">{t('common.active')}</span>
                        <span className="lb">{l.label || t('intakeLink.noLabel')}</span>
                        <span className="mt">{t('trackingLink.expires', { date: toLocalDateString(l.expiresAt, 'dd.MM HH:mm') })}</span>
                        <button type="button" className="rv" onClick={() => setLinkToRevoke(l)}>{t('intakeLink.revoke')}</button>
                    </div>
                ))}
                {recent.map((l) => (
                    <div className="ilk-row" key={l.id} style={{ opacity: 0.75 }}>
                        <span className={`st ${l.status}`}>{statusLabel(l.status)}</span>
                        <span className="lb">{l.label || t('intakeLink.noLabel')}</span>
                        <span className="mt">{toLocalDateString(l.usedAt || l.revokedAt || l.expiresAt, 'dd.MM HH:mm')}</span>
                    </div>
                ))}
            </div>
            <ConfirmationModal display={!!linkToRevoke} toggle={() => setLinkToRevoke(null)}
                title={t('intakeLink.cancelLinkTitle')} confirmText={t('intakeLink.cancelLink')} cancelText={t('intakeLink.keepIt')}
                message={linkToRevoke?.label ? t('intakeLink.cancelLinkMessageLabel', { label: linkToRevoke.label }) : t('intakeLink.cancelLinkMessage')}
                confirmAction={() => linkToRevoke && revoke(linkToRevoke.id)} />
        </div>
    );
};

/**
 * Share the company's customer self-entry link: copy it, or open WhatsApp /
 * Viber / email with a short message. Admins can replace the link. Below it,
 * private single-use links for individual phone enquiries.
 */
const IntakeLinkDrawer = ({ show, onHide }) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { data, isLoading } = useQuery(['intake-link'], getIntakeLink, { enabled: show });
    const url = data?.code === 200 ? data.message?.url : '';
    const [message, setMessage] = useState('');
    const [confirmReplace, setConfirmReplace] = useState(false);
    const text = message || DEFAULT_MESSAGE(url);

    const { mutate: regenerate, isLoading: regenerating } = useMutation(regenerateIntakeLink, {
        onSuccess: onMutateSuccess(() => queryClient.invalidateQueries(['intake-link']), t('intakeLink.newLinkCreated')),
    });

    return (
        <SbrDrawer show={show} onHide={onHide} secondary icon={<LinkIcon />} title={t('intakeLink.title')}
            subtitle={t('intakeLink.subtitle')}>
            <style>{styles}</style>
            <div className="ilk-url">
                <input readOnly value={isLoading ? t('common.loading') : url} onFocus={(e) => e.target.select()} />
                <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => copyText(url)} disabled={!url}>{t('intakeLink.copy')}</button>
            </div>

            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)', margin: '14px 0 6px' }}>{t('intakeLink.message')}</div>
            <textarea className="ilk-msg" value={text} onChange={(e) => setMessage(e.target.value)} />
            <div className="ilk-share">
                <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer">WhatsApp</a>
                <a href={`viber://forward?text=${encodeURIComponent(text)}`}>Viber</a>
                <a href={`mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT())}&body=${encodeURIComponent(text)}`}>{t('common.email')}</a>
            </div>
            <div className="ilk-hint">
                <Trans i18nKey="intakeLink.customerHint" components={{ b: <b /> }} />
            </div>

            {isUserAdmin() && (
                <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 8 }}>{t('intakeLink.replaceHint')}</div>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" disabled={regenerating}
                        onClick={() => setConfirmReplace(true)}>
                        {regenerating ? t('intakeLink.replacing') : t('intakeLink.replaceLink')}
                    </button>
                </div>
            )}

            {show && <PrivateLinkSection />}
        <ConfirmationModal display={confirmReplace} toggle={setConfirmReplace}
                title={t('intakeLink.replaceTitle')} confirmText={t('intakeLink.replace')} cancelText={t('intakeLink.keepIt')}
                message={t('intakeLink.replaceMessage')}
                confirmAction={regenerate} />
            </SbrDrawer>
    );
};

export default IntakeLinkDrawer;
